import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import styles from './HomePage.module.css';
import PublicFooter from '../components/molecules/PublicFooter';
import MailerLiteSignupForm from '../components/molecules/MailerLiteSignupForm';

const HomePage = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/passport', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: '/passport' },
    });
  };

  if (isLoading) {
    return <div className={styles.loadingScreen}>Loading...</div>;
  }

  return (
    <div className={styles.homeContainer}>
      <div className={styles.betaBanner}>
        <p>
          <i className="fas fa-flask" style={{ marginRight: '8px' }}></i>
          Ego Protocol is currently in a limited Private Beta.
        </p>
      </div>

      {/* --- 1. Hero Section --- */}
      <section className={`${styles.section} ${styles.heroSection}`}>
        <div className={styles.heroContent}>
          <h1>Reclaim Your Digital Voice.</h1>
          <p className={styles.subtitle}>
            In a world of generic AI, Ego Protocol makes your digital presence
            authentically yours. Create, manage, and deploy AI personas powered
            by your unique personality. The platform is currently in a private
            beta, and access is by invitation only.
          </p>
          <div className={styles.heroActions}>
            <MailerLiteSignupForm />
            <button className={styles.loginLink} onClick={handleLogin}>
              Already have access? <strong>Log In</strong>
            </button>
          </div>
        </div>
        <div className={styles.scrollIndicator}>
          <i className="fas fa-chevron-down"></i>
        </div>
      </section>

      {/* --- 2. The Problem Section --- */}
      <section className={styles.section}>
        <h2>Tired of Sounding Like a Robot?</h2>
        <p className={styles.sectionText}>
          We've all seen it. Generic LinkedIn posts, bland bios, content that
          strips away what makes us unique. The digital world is starting to
          sound like one, big, boring algorithm.{' '}
          <strong>Ego is here to fix that.</strong>
        </p>
      </section>

      {/* --- 3. The Solution Section --- */}
      <section className={`${styles.section} ${styles.solutionSection}`}>
        <h2>Your Personality is the Prompt.</h2>
        <p className={styles.sectionText}>
          Instead of fighting with generic AI, Ego allows you to build a secure
          "Digital Passport" of your unique identity. This becomes the core that
          powers every piece of content you create.
        </p>
        <div className={styles.featureGrid}>
          <div className={styles.featureCard}>
            <i className="fas fa-passport"></i>
            <h4>Build Your Passport</h4>
            <p>
              Define your core identity, from personality traits to life
              experiences, in your private, secure Digital Passport.
            </p>
          </div>
          <div className={styles.featureCard}>
            <i className="fas fa-user-friends"></i>
            <h4>Craft Your Personas</h4>
            <p>
              Create specialized AI versions of yourself—a professional for your
              website, a creative for social media, or a helpful concierge.
            </p>
          </div>
          <div className={styles.featureCard}>
            <i className="fas fa-share-nodes"></i>
            <h4>Deploy with Authenticity</h4>
            <p>
              Chat, generate content, or build custom applications. Every
              interaction is infused with your unique voice, guaranteed.
            </p>
          </div>
        </div>
      </section>

      {/* --- 4. The Use Cases Section --- */}
      <section className={styles.section}>
        <h2>One Platform, Limitless Use Cases.</h2>
        <div className={styles.useCaseGrid}>
          <div className={styles.useCaseCard}>
            <h4>For Professionals</h4>
            <p>
              Generate authentic LinkedIn posts, cover letters, and bios that
              sound like you, not a template. Let your AI handle the first
              draft, infused with your expertise and tone.
            </p>
          </div>
          <div className={styles.useCaseCard}>
            <h4>For Creators</h4>
            <p>
              Brainstorm ideas, generate unique images, and overcome creative
              blocks with an AI partner that truly understands your artistic
              style and vision.
            </p>
          </div>
          <div className={styles.useCaseCard}>
            <h4>For Developers (Coming Soon)</h4>
            <p>
              Integrate your authentic AI into any application with our powerful
              API. Build custom chatbots, automated agents, and more.
            </p>
          </div>
        </div>
      </section>
      <section className={`${styles.section} ${styles.ctaSection}`}>
        <h2>Ready to Build Your Digital Self?</h2>
        <br />
        <div className={styles.heroActions}>
          <MailerLiteSignupForm />
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default HomePage;
